/**
 * 刊登图转 SHEIN 地址：优先 transform-pic（只传公网 URL），失败再读字节 upload-pic。
 * 原图超过平台 3MB 时跳过 transform-pic，本机压缩后再 upload-pic。
 */

import { sheinPlatformAPI } from '@/utils/api';
import { resolveImageUrlToBlob, stripImageFileExtension } from '@/utils/imageUrlUtils';
import { toManagedCanonicalUploadsUrl } from '@/utils/imageDisplayAccess';
import {
  SHEIN_PRODUCT_IMAGE_MAX_BYTES,
  prepareSheinProductImage,
} from '@/shein-platform/utils/sheinProductImage';
import { toCanonicalUploadsPath } from '@/shein-platform/modules/products/components/publishImageUploadThumbSrc';

/**
 * 刊登预组：商品级并行度。
 * 发品规范等已做跨条目缓存；略提高吞吐，仍避免打满限流。
 */
export const LISTING_PREPARE_CONVERT_CONCURRENCY = 6;

function asText(value) {
  return String(value || '').trim();
}

function fileNameFromUrl(url) {
  const raw = asText(url);
  if (!raw) return 'product.jpg';
  try {
    const path = new URL(raw, 'https://local.invalid').pathname;
    return decodeURIComponent((path.split('/').pop() || '').split('?')[0]) || 'product.jpg';
  } catch {
    return (raw.split('/').pop() || '').split('?')[0] || 'product.jpg';
  }
}

function looksLikeSheinHostedUrl(url) {
  const lower = asText(url).toLowerCase();
  return lower.includes('shein.com')
    || lower.includes('sheincorp.')
    || lower.includes('ltwebstatic.com')
    || lower.includes('lt-pqms.')
    || lower.includes('imgdeal');
}

export function looksLikeSheinImageTooLargeError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  if (!message) return false;
  return message.includes('3mb')
    || message.includes('3 mb')
    || message.includes('too large')
    || message.includes('size limit')
    || (message.includes('exceed') && message.includes('size'))
    || (message.includes('超过') && (message.includes('大') || message.includes('大小') || message.includes('限制')));
}

async function probeRemoteContentLength(url) {
  const source = asText(url);
  if (!source || source.startsWith('blob:') || source.startsWith('data:')) return null;
  try {
    const res = await fetch(source, { method: 'HEAD', mode: 'cors' });
    const len = Number(res.headers.get('content-length') || 0);
    return Number.isFinite(len) && len > 0 ? len : null;
  } catch {
    return null;
  }
}

function packConverted(source, nameHint, res) {
  const sheinUrl = asText(res?.sheinUrl || res?.url);
  if (!sheinUrl) return null;
  const rawName = asText(nameHint) || fileNameFromUrl(source);
  const rawIpe = asText(res?.ipeUrl);
  const rawPublic = asText(res?.publicUrl);
  const durable = toCanonicalUploadsPath(rawIpe)
    || toCanonicalUploadsPath(rawPublic)
    || toManagedCanonicalUploadsUrl(source)
    || rawIpe
    || rawPublic
    || source;
  return {
    name: stripImageFileExtension(rawName) || rawName,
    sheinUrl,
    ipeUrl: durable,
    publicUrl: durable,
    previewUrl: durable,
    url: durable,
  };
}

/**
 * @param {{ shopId: string|number, url: string, imageType?: number, nameHint?: string }} params
 * @returns {Promise<{ sheinUrl: string, ipeUrl: string, publicUrl: string, previewUrl: string, name: string }|null>}
 */
export async function convertListingImageToShein({
  shopId,
  url,
  imageType = 1,
  nameHint = '',
} = {}) {
  const source = asText(url);
  if (!shopId || !source) return null;
  if (looksLikeSheinHostedUrl(source)) {
    return packConverted(source, nameHint, { sheinUrl: source, ipeUrl: source, publicUrl: source });
  }

  const remoteBytes = await probeRemoteContentLength(source);
  const skipTransform = remoteBytes != null && remoteBytes > SHEIN_PRODUCT_IMAGE_MAX_BYTES;
  if (!skipTransform) {
    try {
      const transformed = await sheinPlatformAPI.transformProductPic(shopId, {
        originalUrl: source,
        imageType,
      });
      const packed = packConverted(source, nameHint, transformed);
      if (packed) return packed;
    } catch (error) {
      if (!looksLikeSheinImageTooLargeError(error)) {
        /* SHEIN 拉不到图时再读字节 upload-pic */
      }
    }
  }

  const blob = await resolveImageUrlToBlob(source);
  if (!blob) return null;
  const rawName = asText(nameHint) || fileNameFromUrl(source);
  const file = new File(
    [blob],
    rawName.includes('.') ? rawName : `${rawName || 'product'}.jpg`,
    { type: blob.type || 'image/jpeg', lastModified: Date.now() },
  );
  const prepared = await prepareSheinProductImage(file);
  // 原图已在图片空间；裁剪后仅转 SHEIN，勿再落 OSS
  const res = await sheinPlatformAPI.uploadProductPic(shopId, prepared, {
    imageType,
    fromImageSpace: true,
  });
  return packConverted(source, nameHint, res);
}
